import { Router } from "express";
import { AllocationRequestController } from "../controllers/allocationRequest.controller";
import { Routes } from "../../../interface/routes.interface";
import { permissionInjector } from "../../../middlewares/permissionInjector";
import { authMiddleware } from "middlewares/authMiddleware";

/**
 * @swagger
 * tags:
 *   name: AllocationRequest
 *   description: Allocation Request Management APIs
 */

export class AllocationRequestRouter implements Routes {
    public router = Router();
    public allocationRequestController = new AllocationRequestController();
    public path = '/allocation-request';

    constructor() {
        this.initializeRoutes();
    }

    public initializeRoutes() {
        /**
         * @swagger
         * /allocation-request:
         *   post:
         *     summary: Create a new allocation request
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               userIds:
         *                 type: array
         *                 description: IDs of the users (labourers) for the allocation request
         *                 items:
         *                   type: string
         *                   example: "507f1f77bcf86cd799439011"
         *               startDate:
         *                 type: string
         *                 format: date-time
         *                 description: Start date of the allocation period
         *                 example: "2025-07-11T00:00:00.000Z"
         *               allocationNotes:
         *                 type: string
         *                 description: Notes for the allocation request
         *                 example: "Urgent project requiring skilled laborers"
         *               endDate:
         *                 type: string
         *                 format: date-time
         *                 description: End date of the allocation period
         *                 example: "2025-07-12T00:00:00.000Z"
         *               wage:
         *                 type: number
         *                 format: float
         *                 description: Wage offered for the work
         *                 example: 30.5
         *               jobId:
         *                 type: string
         *                 description: ID of the job
         *                 example: "685a7043fab4351960176aec"
         *             required:
         *               - userIds
         *               - startDate
         *               - endDate
         *               - jobId
         *     responses:
         *       201:
         *         description: Allocation request created successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: Created
         *                 message:
         *                   type: string
         *                   example: Allocation request created successfully
         *                 data:
         *                   type: array
         *                   items:
         *                     $ref: '#/components/schemas/AllocationRequest'
         *       400:
         *         description: Bad request
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.post(`${this.path}/`, permissionInjector(`${this.path}`, 'POST'), this.allocationRequestController.createAllocationRequest);

        /**
         * @swagger
         * /allocation-request:
         *   get:
         *     summary: Get all allocation requests
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           default: 1
         *         description: Page number for pagination
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           default: 10
         *         description: Number of records per page
         *       - in: query
         *         name: sort
         *         schema:
         *           type: string
         *         description: Sorting criteria
         *       - in: query
         *         name: userId
         *         schema:
         *           type: string
         *         description: Filter by user ID
         *       - in: query
         *         name: allocationStatus
         *         schema:
         *           type: string
         *           enum: [PENDING, ACCEPTED, REJECTED]
         *         description: Filter by allocation status
         *       - in: query
         *         name: filters
         *         schema:
         *           type: string
         *         description: JSON string of additional filters
         *     responses:
         *       200:
         *         description: List of allocation requests
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   type: array
         *                   items:
         *                     type: object
         *                     properties:
         *                       _id:
         *                         type: string
         *                         
         *                         description: Unique identifier for the allocation request
         *                         example: 685a7043fab4351960176aec
         *                       requestedDte:
         *                         type: string
         *                         format: date-time
         *                         description: Date when the request was made
         *                         example: 2025-06-24T09:30:43.761Z
         *                       companyName:
         *                         type: string
         *                         description: Name of the company
         *                         example: Gard-Labour-Hire
         *                       startDate:
         *                         type: string
         *                         format: date-time
         *                         description: Start date of the allocation period
         *                         example: 2025-07-11T00:00:00.000Z
         *                       endDate:
         *                         type: string
         *                         format: date-time
         *                         description: End date of the allocation period
         *                         example: 2025-07-12T00:00:00.000Z
         *                       wages:
         *                         type: number
         *                         description: Wage offered
         *                         example: 30
         *                       location:
         *                         type: string
         *                         description: Location of the allocation
         *                         example: Sydney, NSW
         *                       email:
         *                         type: string
         *                         format: email
         *                         description: Email of the user
         *                         example: labour3@gmail.com
         *                       firstName:
         *                         type: string
         *                         description: First name of the user
         *                         example: Updated
         *                       lastName:
         *                         type: string
         *                         description: Last name of the user
         *                         example: Labour
         *                       status:
         *                         type: string
         *                         description: Status of the allocation request
         *                         example: pending
         *                       phone:
         *                         type: string
         *                         description: Phone number of the user
         *                         example: 7047858741
         *                       countryCode:
         *                         type: string
         *                         description: Country code of the phone number
         *                         example: +91
         *                 total:
         *                   type: integer
         *                   description: Total number of records
         *                   example: 3
         *                 page:
         *                   type: integer
         *                   description: Current page number
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   description: Total number of pages
         *                   example: 1
         *                 statusCounts:
         *                   type: object
         *                   properties:
         *                     pending:
         *                       type: integer
         *                       description: Number of pending requests
         *                       example: 2
         *                     accepted:
         *                       type: integer
         *                       description: Number of accepted requests
         *                       example: 0
         *                     rejected:
         *                       type: integer
         *                       description: Number of rejected requests
         *                       example: 1
         *       400:
         *         description: Bad request
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.get(`${this.path}`, permissionInjector(`${this.path}`, 'GET'), this.allocationRequestController.getAllAllocationRequests);

        /**
         * @swagger
         * /allocation-request/{id}:
         *   get:
         *     summary: Get allocation request by ID
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *           
         *         description: ID of the allocation request
         *     responses:
         *       200:
         *         description: Allocation request found
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   type: object
         *                   properties:
         *                     _id:
         *                       type: string
         *                       
         *                       description: Unique identifier for the allocation request
         *                       example: 685a7043fab4351960176aec
         *                     requestedDte:
         *                       type: string
         *                       format: date-time
         *                       description: Date when the request was made
         *                       example: 2025-06-24T09:30:43.761Z
         *                     companyName:
         *                       type: string
         *                       description: Name of the company
         *                       example: Gard-Labour-Hire
         *                     startDate:
         *                       type: string
         *                       format: date-time
         *                       description: Start date of the allocation period
         *                       example: 2025-07-11T00:00:00.000Z
         *                     message:
         *                       type: string
         *                       description: Message for the allocation request
         *                       example: We need you for a project
         *                     endDate:
         *                       type: string
         *                       format: date-time
         *                       description: End date of the allocation period
         *                       example: 2025-07-12T00:00:00.000Z
         *                     wages:
         *                       type: number
         *                       description: Wage offered
         *                       example: 30
         *                     location:
         *                       type: string
         *                       description: Location of the allocation
         *                       example: Sydney, NSW
         *                     email:
         *                       type: string
         *                       format: email
         *                       description: Email of the user
         *                       example: labour3@gmail.com
         *                     firstName:
         *                       type: string
         *                       description: First name of the user
         *                       example: Updated
         *                     lastName:
         *                       type: string
         *                       description: Last name of the user
         *                       example: Labour
         *                     status:
         *                       type: string
         *                       description: Status of the allocation request
         *                       example: pending
         *                     phone:
         *                       type: string
         *                       description: Phone number of the user
         *                       example: 7047858741
         *                     countryCode:
         *                       type: string
         *                       description: Country code of the phone number
         *                       example: +91
         *       404:
         *         description: Allocation request not found
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.get(`${this.path}/:id`, permissionInjector(`${this.path}`, 'GET'), this.allocationRequestController.getAllocationRequestById);

        /**
         * @swagger
         * /allocation-request/{id}:
         *   put:
         *     summary: Update an allocation request
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *           
         *         description: ID of the allocation request to update
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               userId:
         *                 type: string
         *                 
         *                 description: ID of the user (labourer) for the allocation request
         *               message:
         *                 type: string
         *                 description: Optional message for the allocation request
         *               startDate:
         *                 type: string
         *                 format: date-time
         *                 description: Updated start date of the allocation period
         *               endDate:
         *                 type: string
         *                 format: date-time
         *                 description: Updated end date of the allocation period
         *               location:
         *                 type: string
         *                 description: Updated location of the work
         *               wage:
         *                 type: number
         *                 format: float
         *                 description: Updated wage offered for the work
         *               companyName:
         *                 type: string
         *                 example: Gard-Labour-Hire
         *             required:
         *               - at least one update field
         *     responses:
         *       200:
         *         description: Allocation request updated successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   $ref: '#/components/schemas/AllocationRequest'
         *       400:
         *         description: Bad request or no update fields provided
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden (e.g., insufficient permissions)
         *       404:
         *         description: Allocation request not found
         *       500:
         *         description: Internal server error
         */
        this.router.put(`${this.path}/:id`, permissionInjector(`${this.path}`, 'PUT'), this.allocationRequestController.updateAllocationRequest);

        /**
         * @swagger
         * /allocation-request/{id}/respond:
         *   put:
         *     summary: Respond to an allocation request
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the allocation request
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               allocationStatus:
         *                 type: string
         *                 enum: [ACCEPTED, REJECTED]
         *                 description: Status of the response
         *               responseNotes:
         *                 type: string
         *                 description: Notes for the response
         *             required:
         *               - allocationStatus
         *     responses:
         *       200:
         *         description: Allocation request updated
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   $ref: '#/components/schemas/AllocationRequest'
         *       400:
         *         description: Bad request
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       404:
         *         description: Allocation request not found
         *       500:
         *         description: Internal server error
         */
        this.router.put(`${this.path}/:id/respond`, authMiddleware, permissionInjector(`${this.path}/:id/respond`, 'PUT'), this.allocationRequestController.respondToAllocationRequest);

        // /**
        //  * @swagger
        //  * /allocation-request/history:
        //  *   get:
        //  *     summary: Get allocation request history
        //  *     tags: [AllocationRequest]
        //  *     security:
        //  *       - bearerAuth: []
        //  *     parameters:
        //  *       - in: query
        //  *         name: page
        //  *         schema:
        //  *           type: integer
        //  *           default: 1
        //  *         description: Page number for pagination
        //  *       - in: query
        //  *         name: limit
        //  *         schema:
        //  *           type: integer
        //  *           default: 10
        //  *         description: Number of records per page
        //  *       - in: query
        //  *         name: sort
        //  *         schema:
        //  *           type: string
        //  *         description: Sorting criteria
        //  *       - in: query
        //  *         name: userId
        //  *         schema:
        //  *           type: string
        //  *           
        //  *         description: Filter by user ID
        //  *       - in: query
        //  *         name: filters
        //  *         schema:
        //  *           type: string
        //  *         description: JSON string of additional filters
        //  *     responses:
        //  *       200:
        //  *         description: Allocation request history retrieved
        //  *         content:
        //  *           application/json:
        //  *             schema:
        //  *               type: object
        //  *               properties:
        //  *                 status:
        //  *                   type: string
        //  *                   example: OK
        //  *                 message:
        //  *                   type: string
        //  *                 data:
        //  *                   type: array
        //  *                   items:
        //  *                     $ref: '#/components/schemas/AllocationRequest'
        //  *                 total:
        //  *                   type: integer
        //  *                 page:
        //  *                   type: integer
        //  *                 totalPage:
        //  *                   type: integer
        //  *       401:
        //  *         description: Unauthorized
        //  *       500:
        //  *         description: Internal server error
        //  */
        this.router.get(`${this.path}/history`, permissionInjector(`${this.path}/history`, 'GET'), this.allocationRequestController.getAllocationHistory);

        /**
         * @swagger
         * /allocation-request:
         *   delete:
         *     summary: Delete an allocation request
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             required: [ids]
         *             properties:
         *               ids:
         *                 type: array
         *                 items:
         *                   type: string
         *                   
         *                 example: ["60f6c0f2e13e3c1e4c8e3a6b"]
         *     responses:
         *       200:
         *         description: Allocation request deleted
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   type: object
         *                   properties:
         *                     deleted:
         *                       type: integer
         *                       description: Number of deleted requests
         *                       example: 1
         *       404:
         *         description: Allocation request not found
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.delete(`${this.path}`, permissionInjector(`${this.path}`, 'DELETE'), this.allocationRequestController.deleteAllocationRequest);
        /**
         * @swagger
         * /allocation-request/user/{userId}/pending:
         *   get:
         *     summary: Get pending allocation requests for a specific user
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: userId
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the user to retrieve pending allocation requests for
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           default: 1
         *         description: Page number for pagination
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           default: 10
         *         description: Number of records per page
         *       - in: query
         *         name: sort
         *         schema:
         *           type: string
         *         description: Sorting criteria
         *     responses:
         *       200:
         *         description: List of pending allocation requests for the user
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                   example: Allocation Request retrieved successfully
         *                 data:
         *                   type: array
         *                   items:
         *                     type: object
         *                     properties:
         *                       _id:
         *                         type: string
         *                         description: Unique identifier for the allocation request
         *                         example: 685a7043fab4351960176aec
         *                       requestedDte:
         *                         type: string
         *                         format: date-time
         *                         description: Date when the request was made
         *                         example: 2025-06-24T09:30:43.761Z
         *                       companyName:
         *                         type: string
         *                         description: Name of the company
         *                         example: Gard-Labour-Hire
         *                       startDate:
         *                         type: string
         *                         format: date-time
         *                         description: Start date of the allocation period
         *                         example: 2025-07-11T00:00:00.000Z
         *                       endDate:
         *                         type: string
         *                         format: date-time
         *                         description: End date of the allocation period
         *                         example: 2025-07-12T00:00:00.000Z
         *                       wages:
         *                         type: number
         *                         description: Wage offered
         *                         example: 30
         *                       location:
         *                         type: string
         *                         description: Location of the allocation
         *                         example: Sydney, NSW
         *                       email:
         *                         type: string
         *                         format: email
         *                         description: Email of the user
         *                         example: labour3@gmail.com
         *                       firstName:
         *                         type: string
         *                         description: First name of the user
         *                         example: Updated
         *                       lastName:
         *                         type: string
         *                         description: Last name of the user
         *                         example: Labour
         *                       status:
         *                         type: string
         *                         description: Status of the allocation request
         *                         example: PENDING
         *                       phone:
         *                         type: string
         *                         description: Phone number of the user
         *                         example: 7047858741
         *                       countryCode:
         *                         type: string
         *                         description: Country code of the phone number
         *                         example: +91
         *                       profileImage:
         *                         type: object
         *                         nullable: true
         *                         properties:
         *                           _id:
         *                             type: string
         *                             description: ID of the profile image
         *                             example: 507f1f77bcf86cd799439011
         *                           filename:
         *                             type: string
         *                             description: Name of the profile image file
         *                             example: profile.jpg
         *                           url:
         *                             type: string
         *                             description: URL of the profile image
         *                             example: https://s3.amazonaws.com/bucket/profile.jpg
         *                       jobStatus:
         *                         type: string
         *                         description: Status of the associated job
         *                         example: Open
         *                       note:
         *                         type: string
         *                         description: Note associated with the job
         *                         example: Urgent project
         *                       allocationNotes:
         *                         type: string
         *                         description: Notes for the allocation request
         *                 total:
         *                   type: integer
         *                   description: Total number of pending allocation requests
         *                   example: 3
         *                 page:
         *                   type: integer
         *                   description: Current page number
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   description: Total number of pages
         *                   example: 1
         *                 count:
         *                   type: object
         *                   properties:
         *                     pending:
         *                       type: integer
         *                       description: Number of pending requests
         *                       example: 3
         *                     accepted:
         *                       type: integer
         *                       description: Number of accepted requests
         *                       example: 0
         *                     rejected:
         *                       type: integer
         *                       description: Number of rejected requests
         *                       example: 0
         *       400:
         *         description: Bad request (e.g., invalid userId)
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}/user/:userId/pending`,
            authMiddleware,
            permissionInjector(`${this.path}`, 'GET'),
            this.allocationRequestController.getPendingAllocationRequestsForUser
        );

        /**
         * @swagger
         * /allocation-request/alloted/{jobId}:
         *   get:
         *     summary: Get all allotted labourers for a specific job
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: jobId
         *         required: true
         *         schema:
         *           type: string
         *           
         *         description: ID of the job to retrieve allotted labourers for
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           default: 1
         *         description: Page number for pagination
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           default: 10
         *         description: Number of records per page
         *       - in: query
         *         name: sort
         *         schema:
         *           type: string
         *         description: Sorting criteria 
         *     responses:
         *       200:
         *         description: List of allotted labourers for the job
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 data:
         *                   type: array
         *                   items:
         *                     type: object
         *                     properties:
         *                       employeeId:
         *                         type: string
         *                         description: Employee ID of the labourer
         *                         example: LBR-00123
         *                       firstName:
         *                         type: string
         *                         description: First name of the labourer
         *                         example: John
         *                       lastName:
         *                         type: string
         *                         description: Last name of the labourer
         *                         example: Doe
         *                       email:
         *                         type: string
         *                         format: email
         *                         description: Email address of the labourer
         *                         example: john.doe@example.com
         *                       allocationRequestId:
         *                         type: string
         *                         description: ID of the allocation request
         *                         example: 685a7043fab4351960176aec
         *                       allocationNotes:
         *                        type: string
         *                       profileImage:
         *                         type: object
         *                         nullable: true
         *                         properties:
         *                           _id:
         *                             type: string
         *                             
         *                             description: ID of the profile image
         *                             example: 507f1f77bcf86cd799439011
         *                           filename:
         *                             type: string
         *                             description: Name of the profile image file
         *                             example: profile.jpg
         *                           url:
         *                             type: string
         *                             description: URL of the profile image
         *                             example: https://s3.amazonaws.com/bucket/profile.jpg
         *                           fileSize:
         *                             type: number
         *                             description: Size of file
         *                 total:
         *                   type: integer
         *                   description: Total number of allotted labourers
         *                   example: 5
         *                 page:
         *                   type: integer
         *                   description: Current page number
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   description: Total number of pages
         *                   example: 1
         *       400:
         *         description: Bad request (e.g., invalid jobId)
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.get(`${this.path}/alloted/:jobId`, permissionInjector(`${this.path}`, 'GET'), this.allocationRequestController.getAllottedLabourers);

        /**
         * @swagger
         * /allocation-request/requested/{jobId}:
         *   get:
         *     summary: Get all requested labourers for a specific job
         *     tags: [AllocationRequest]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: jobId
         *         required: true
         *         schema:
         *           type: string
         *           
         *         description: ID of the job to retrieve requested labourers for
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           default: 1
         *         description: Page number for pagination
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           default: 10
         *         description: Number of records per page
         *       - in: query
         *         name: sort
         *         schema:
         *           type: string
         *         description: Sorting criteria
         *     responses:
         *       200:
         *         description: List of requested labourers for the job
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 data:
         *                   type: array
         *                   items:
         *                     type: object
         *                     properties:
         *                       employeeId:
         *                         type: string
         *                         description: Employee ID of the labourer
         *                         example: LBR-00123
         *                       firstName:
         *                         type: string
         *                         description: First name of the labourer
         *                         example: John
         *                       lastName:
         *                         type: string
         *                         description: Last name of the labourer
         *                         example: Doe
         *                       email:
         *                         type: string
         *                         format: email
         *                         description: Email address of the labourer
         *                         example: john.doe@example.com
         *                       allocationRequestId:
         *                         type: string
         *                         description: ID of the allocation request
         *                         example: 685a7043fab4351960176aec
         *                       status:
         *                         type: string
         *                         enum: [PENDING, REJECTED]
         *                         description: Status of the allocation request
         *                         example: PENDING
         *                       userId:
         *                         type: string
         *                         description: ID of the user (labourer)
         *                         example: 507f1f77bcf86cd799439011
         *                       jobId:
         *                         type: string
         *                         description: ID of the job
         *                         example: 6863981fd448a60209430ed9
         *                       allocationNotes:
         *                         type: string
         *                       profileImage:
         *                         type: object
         *                         nullable: true
         *                         properties:
         *                           _id:
         *                             type: string
         *                             description: ID of the profile image
         *                             example: 507f1f77bcf86cd799439011
         *                           filename:
         *                             type: string
         *                             description: Name of the profile image file
         *                             example: profile.jpg
         *                           url:
         *                             type: string
         *                             description: URL of the profile image
         *                             example: https://s3.amazonaws.com/bucket/profile.jpg
         *                 total:
         *                   type: integer
         *                   description: Total number of requested labourers
         *                   example: 5
         *                 page:
         *                   type: integer
         *                   description: Current page number
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   description: Total number of pages
         *                   example: 1
         *       400:
         *         description: Bad request (e.g., invalid jobId)
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */

        this.router.get(`${this.path}/requested/:jobId`, permissionInjector(`${this.path}`, 'GET'), this.allocationRequestController.getRequestedLabourers);

    }
}
/**
 * @swagger
 * components:
 *   schemas:
 *     AllocationRequest:
 *       type: object
 *       properties:
 *         _id:
 *           type: string
 *           description: Unique identifier for the allocation request
 *           example: 60f6c0f2e13e3c1e4c8e3a6b
 *         userId:
 *           type: object
 *           properties:
 *             _id:
 *               type: string
 *               description: Unique identifier for the user
 *               example: 507f1f77bcf86cd799439011
 *             firstName:
 *               type: string
 *               description: First name of the user
 *               example: John
 *             lastName:
 *               type: string
 *               description: Last name of the user
 *               example: Doe
 *             email:
 *               type: string
 *               format: email
 *               description: Email address of the user
 *               example: john.doe@example.com
 *             phone:
 *               type: string
 *               description: Phone number of the user
 *               example: 7047858741
 *             countryCode:
 *               type: string
 *               description: Country code of the phone number
 *               example: +91
 *         message:
 *           type: string
 *           description: Optional message for the allocation request
 *           example: We need you for a 3-day project in Surat.
 *         requestedDte:
 *           type: string
 *           format: date-time
 *           description: Date when the request was made
 *           example: 2025-06-24T09:30:43.761Z
 *         startDate:
 *           type: string
 *           format: date-time
 *           description: Start date of the allocation period
 *           example: 2025-07-01T09:00:00Z
 *         endDate:
 *           type: string
 *           format: date-time
 *           description: End date of the allocation period
 *           example: 2025-07-03T18:00:00Z
 *         location:
 *           type: string
 *           description: Location of the work
 *           example: Surat
 *         wage:
 *           type: number
 *           format: float
 *           description: Wage offered for the work
 *           example: 750.5
 *         companyName:
 *           type: string
 *           description: Name of the company
 *           example: Gard-Labour-Hire
 *         status:
 *           type: string
 *           enum: [PENDING, ACCEPTED, REJECTED]
 *           description: Status of the allocation request
 *           example: PENDING
 *         createdBy:
 *           type: object
 *           properties:
 *             _id:
 *               type: string
 *               
 *               description: Unique identifier for the user who created the request
 *               example: 507f1f77bcf86cd799439022
 *             firstName:
 *               type: string
 *               description: First name of the creator
 *               example: Manager
 *             lastName:
 *               type: string
 *               description: Last name of the creator
 *               example: Smith
 *         responseNotes:
 *           type: string
 *           description: Notes added when responding to the request
 *           example: Labour accepted the request
 *         allocationNotes:
 *           type: string
 *           description: Notes added when send request to the Labour's
 *         respondedAt:
 *           type: string
 *           format: date-time
 *           description: Timestamp when the request was responded to
 *           example: 2025-07-02T12:00:00Z
 *         labour:
 *           type: object
 *           properties:
 *             employee_id:
 *               type: string
 *               description: Employee ID of the labour
 *               example: LBR-00123
 *             skills:
 *               type: array
 *               description: List of skills of the labour
 *               items:
 *                 type: string
 *                 example: Masonry
 *       required:
 *         - userId
 *         - startDate
 *         - endDate
 *         - location
 *         - createdBy
 *         - status
 */